import { CONTACT } from "@/lib/contact";

export const brand = {
    name: "SalesCan",
    tagline: "Founder-led from St. Catharines since 2022",
    email: CONTACT.email,
    phone: CONTACT.phoneDisplay,
    phoneHref: CONTACT.phoneTel,
    address: CONTACT.address,
};

export const nav = [
    { label: "Pricing", href: "/pricing" },
    { label: "Products", href: "/products" },
    { label: "Work", href: "/#work" },
    { label: "About", href: "/#about" },
    { label: "Contact", href: "/#contact" },
];

export const diagnostic = {
    label: "Book the diagnostic",
    short: "Diagnostic",
    hint: "30 minutes. You leave with the leak and a price — whether you hire us or not.",
};

export const hero = {
    kicker: "St. Catharines · Building since 2022",
    meta: "// AI software · SaaS products · Automation",
    title: "We build AI-powered SaaS, websites & automation real businesses run on.",
    subtitle:
        "SalesCan designs, engineers and operates its own AI-powered SaaS products — and builds websites, chatbots, CRM automation and business workflows for Canadian businesses. Founder-led, fixed-fee, no long-term contracts.",
    primaryCta: "Start a project",
    secondaryCta: "See products",
    meterCta: "What is manual work costing you? Try the 10-second meter ↓",
};

export const marqueeTags = [
    "Founder-led from Niagara",
    "Building since 2022",
    "14 SaaS products",
    "40+ client websites",
    "AI Pipeline Automation",
    "CRM Optimization",
    "Predictive Revenue Analytics",
    "24/7 Lead Qualification",
];

export const roiDefaults = {
    leads: 80,
    dealSize: 5000,
    closeRate: 18,
    lift: 42,
};

export const services = {
    kicker: "What we build for you",
    title: "Three engines. One revenue machine.",
    items: [
        {
            index: "01",
            cat: "Analytics",
            icon: "LineChart",
            title: "Predictive Revenue Analytics",
            desc: "Uncover hidden friction in your pipeline with real-time funnel intelligence, forecast accuracy dashboards and conversion leakage alerts — so every decision is backed by data, not gut feel.",
            image: "https://images.pexels.com/photos/27141314/pexels-photo-27141314.jpeg?auto=compress&cs=tinysrgb&w=900",
        },
        {
            index: "02",
            cat: "Automations",
            icon: "Workflow",
            title: "Hyper-Automated Sales Workflows",
            desc: "Eliminate manual data entry. Auto-enrich leads, sequence follow-ups and trigger instant meeting bookings while your reps focus on closing.",
            image: "https://images.pexels.com/photos/27141307/pexels-photo-27141307.jpeg?auto=compress&cs=tinysrgb&w=900",
        },
        {
            index: "03",
            cat: "AI Agents",
            icon: "Bot",
            title: "Autonomous AI Sales Copilots",
            desc: "Deploy custom AI agents trained on your highest-performing scripts. They qualify leads around the clock, answer objections in real time, and hand off warm prospects to your team — 24/7.",
            image: "https://images.pexels.com/photos/8386440/pexels-photo-8386440.jpeg?auto=compress&cs=tinysrgb&w=900",
        },
    ],
};

export const method = {
    kicker: "The SalesCan Method",
    title: "Four chapters to sales velocity.",
    subtitle:
        "No fluff, no 90-slide decks. A disciplined operating system that turns your sales process into a compounding asset.",
    steps: [
        {
            no: "01",
            title: "Friction & Process Audit",
            desc: "We dissect your existing deal flows, CRM bottlenecks and conversion leakages. You get a brutally honest map of where revenue is slipping away.",
        },
        {
            no: "02",
            title: "Architecture & Automation",
            desc: "We build custom multi-channel outreach, CRM syncing and automated follow-up triggers — engineered around how your buyers actually buy.",
        },
        {
            no: "03",
            title: "AI Copilot Integration",
            desc: "We train AI copilots on your best reps' closing frameworks, so every lead gets an instant, on-brand, high-converting response.",
        },
        {
            no: "04",
            title: "Continuous Velocity Scaling",
            desc: "Real-time dashboard tracking, A/B testing of sales scripts and aggressive revenue scaling. Your engine gets faster every single month.",
        },
    ],
};

export const contactCopy = {
    kicker: "Book the diagnostic",
    title: "Your pipeline diagnostic is free.",
    subtitle:
        "Thirty minutes. Zero commitment. You leave with the bottleneck and a price — whether you hire us or not.",
    goals: ["Revenue Analytics", "Sales Automation", "AI Copilots", "All-in-One Engine"],
};

export const mission = {
    kicker: "Precision revenue growth, at speed.",
    lead: "Build a pipeline that",
    words: ["Compounds.", "Converts.", "Scales."],
    body: "SalesCan is William Narrie's revenue company, built from St. Catharines since 2022. We engineer the systems that generate demand, capture it, follow up, and close — then we stay on the ones that keep paying. You own the work. No long contracts.",
    cta: "Build Your Revenue Engine",
};

export const capabilities = {
    kicker: "Our services",
    title: "Sales performance services, end to end.",
    subtitle:
        "Not a scattered list of tactics — one connected revenue system. We plug into how your buyers actually buy and engineer growth at every stage.",
    items: [
        { icon: "Users", title: "Customer Acquisition & Retention", desc: "We do more than fill the top of your funnel. We build buying experiences that turn first-time deals into loyal, expanding accounts." },
        { icon: "Search", title: "Inbound & Search Visibility", desc: "Intent-based keyword research, search-optimized content and authority building so qualified buyers find you across search engines and AI assistants." },
        { icon: "Target", title: "Conversion Rate Optimization", desc: "We map your buyer journey and tighten every step — turning more traffic into booked meetings and more meetings into closed revenue." },
        { icon: "Crosshair", title: "Competitive Intelligence", desc: "We dissect your competitors to expose what's working, where they're weak and where the open lanes are — then build systems to outsell them." },
        { icon: "LineChart", title: "Predictive Revenue Analytics", desc: "Our in-house analysts turn campaign and pipeline data into clear signals, so you double down on what converts and cut what doesn't." },
        { icon: "Sparkles", title: "AI Content & Distribution", desc: "Compelling, on-brand messaging distributed at scale that earns attention and turns it into engagement, leads and pipeline." },
        { icon: "Share2", title: "Social & Community Growth", desc: "We turn social and influencer channels into demand engines that build your brand, grow your community and surface real buying signals." },
        { icon: "Palette", title: "Branding", desc: "A product worth buying deserves a brand worth remembering. We craft a distinctive, authentic identity buyers recognize and trust." },
        { icon: "Code2", title: "Conversion-First Web", desc: "Beautiful isn't enough — your site has to perform. We design and build fast, conversion-focused sites that turn visitors into leads." },
        { icon: "Megaphone", title: "Paid Acquisition", desc: "Don't just buy clicks — buy growth. Scroll-stopping creative and tightly targeted campaigns built to attract the right buyers and drive revenue." },
        { icon: "Mail", title: "Lifecycle & Email", desc: "Your inbox is crowded. We build automated sequences that nurture prospects, revive cold leads and keep customers coming back." },
        { icon: "Smartphone", title: "Mobile & App Growth", desc: "Apps don't grow by accident. Rapid experimentation and store optimization that gets you discovered and converts installs into paying users." },
    ],
};

export const stats = {
    items: [
        { label: "Building since", display: "2022" },
        { label: "SaaS products built", value: 14, prefix: "~", suffix: "", decimals: 0 },
        { label: "Client websites", value: 40, prefix: "", suffix: "+", decimals: 0 },
        { label: "Based in", display: "Niagara" },
    ],
};

export const trustedBy = {
    label: "Founder-led from St. Catharines, Ontario — shipping since 2022",
    logos: [
        "14 SaaS products",
        "40+ websites",
        "St. Catharines",
        "Niagara",
        "Ontario",
        "Canada",
        "You own the work",
        "No long contracts",
    ],
};

export const team = {
    kicker: "Meet the team",
    intro: "Our in-house growth team will help you with tailored growth solutions",
    words: ["Prosper,", "Thrive", "& Succeed."],
    subtitle: "Now is the time to work with a growth agency that is data-driven and results-oriented.",
    cta: "Work With Our Team",
    leaders: [
        {
            name: "William Narrie",
            role: "Chief Executive Officer",
            bio: "William has experience growing businesses from 90+ countries. His strengths lie within growth marketing, lead generation, system building & automation.",
            bw: true,
            img: "https://customer-assets-cm19k8pv.emergentagent.net/job_mobile-redesign-66/artifacts/ioqwwf4s_0a889bcf-7fac-4a3asdasdc-83ec-421086dd6c93.png",
        },
        {
            name: "Jonathan Aufray",
            role: "AI Systems Specialist",
            bio: "Jonathan designs and deploys the AI systems that power modern growth — from automated workflows and intelligent lead scoring to custom copilots that help teams scale output without adding headcount.",
            img: "https://www.growth-hackers.net/wp-content/uploads/2023/03/Jonathan-Aufray-Co-founder-_-CEO-Growth-Hackers-Back_White.jpg",
        },
        {
            name: "Danielle Torno",
            role: "Chief Marketing Officer",
            bio: "Danielle started her career as a customer success manager. She has a strong background in project management, communication & business development.",
            img: "https://www.growth-hackers.net/wp-content/uploads/2022/08/Danielle-Torno-Chief-Operating-Officer-COO-Growth-Hackers.jpg",
        },
        {
            name: "Matthew Davis",
            role: "Growth Marketing Strategist",
            bio: "Matt transforms startups, small and medium-sized businesses into flourishing scale-ups. How? By integrating AI-driven marketing tactics with digital transformation.",
            img: "https://www.growth-hackers.net/wp-content/uploads/2024/04/Matthew-Davis-Growth-Marketing-Strategist-at-GrowthHackers-Growth-Hacking-Agency.jpg",
        },
        {
            name: "Francisco Prado",
            role: "VP of Growth",
            bio: "Francisco boasts extensive experience in the realm of growth marketing. His prowess shines in the art of conversion rate optimization and the magic of data analysis.",
            img: "https://www.growth-hackers.net/wp-content/uploads/2025/09/Francisco-Prado-VP-of-Growth-at-Growth-Hackers-modified.jpeg",
        },
        {
            name: "Alex Morgan",
            role: "Head of Demand Generation",
            bio: "Alex spearheads data-backed strategies that harness cutting-edge AI technologies to drive engagement and ensure sustainable business growth.",
            img: "https://www.growth-hackers.net/wp-content/uploads/2026/04/Alex-Morgan-Head-of-Demand-Generation-Growth-Hackers.jpg",
        },
        {
            name: "April Murzo",
            role: "Performance Manager",
            bio: "April drives measurable growth for her clients by turning data into creative direction, tracking KPIs, optimizing campaigns, ensuring every decision is rooted in results & customer success.",
            img: "https://www.growth-hackers.net/wp-content/uploads/2022/08/April-Murzo-Account-Manager-Growth-Hackers.jpg",
        },
        {
            name: "Alec Ali",
            role: "Creative Strategist",
            bio: "Alec is a seasoned creative director elevating brands by crafting compelling stories that not only capture attention but also resonate with their ideal prospects and fuel revenue growth.",
            img: "https://www.growth-hackers.net/wp-content/uploads/2021/09/Alec-Ali-Chief-Client-Success-Officer-Growth-Hackers.jpg",
        },
        {
            name: "Ron Bernardo",
            role: "Content Marketing Director",
            bio: "With over a decade of expertise in content creation and captivating copywriting, Ron empowers businesses to not only boost website traffic but also ignite a steady stream of qualified leads.",
            img: "https://www.growth-hackers.net/wp-content/uploads/2023/09/Ron-Jerome-Bernardo-Content-Marketing-Director-Growth-Hackers-Black_White.jpg",
        },
    ],
    members: [
        {
            name: "Claire Adaza",
            role: "Executive Director",
            img: "https://www.growth-hackers.net/wp-content/uploads/2022/08/Claire-Adaza-Executive-Assistant-Growth-Hackers.jpg",
        },
        {
            name: "John Carl Marlo",
            role: "Multimedia Artist",
            img: "https://www.growth-hackers.net/wp-content/uploads/2024/04/John-Carl-Marlo-Andrade-Graphic-Designer-Video-Editor-GrowthHackers-Growth-Hacking-Company-modified.jpg",
        },
        {
            name: "Aia Ignacio",
            role: "Email Marketing Specialist",
            img: "https://www.growth-hackers.net/wp-content/uploads/2025/07/Aia-Ignacio-Email-Marketing-Specialist-Growth-Hackers.jpg",
        },
        {
            name: "Zap Derrick",
            role: "Business Support Manager",
            img: "https://www.growth-hackers.net/wp-content/uploads/2025/07/Zap-Derrick-Business-Support-Manager-Growth-Hackers.jpg",
        },
    ],
};

export const work = {
    kicker: "Websites we've built",
    title: "Client sites. Real ones go here.",
    subtitle:
        "Forty-plus websites for clients. Drop in the name, industry, and screenshot when you have them — these slots are waiting.",
    items: [
        { id: "slot-01", name: "", industry: "", category: "Websites", image: "", url: "" },
        { id: "slot-02", name: "", industry: "", category: "Websites", image: "", url: "" },
        { id: "slot-03", name: "", industry: "", category: "Websites", image: "", url: "" },
        { id: "slot-04", name: "", industry: "", category: "Websites", image: "", url: "" },
        { id: "slot-05", name: "", industry: "", category: "Websites", image: "", url: "" },
        { id: "slot-06", name: "", industry: "", category: "Websites", image: "", url: "" },
    ],
};

export const faq = {
    kicker: "Questions, answered",
    title: "How SalesCan works with you.",
    items: [
        {
            q: "Who is SalesCan?",
            a: "William Narrie's company. Founder-led from St. Catharines since 2022. We've built roughly 14 SaaS products and 40+ websites for clients. The diagnostic is still with him.",
        },
        {
            q: "What exactly does SalesCan do?",
            a: "We're a sales performance engineering firm. We audit your pipeline, build the automation and AI systems that remove friction, then optimize them continuously — so your revenue grows predictably instead of by luck.",
        },
        {
            q: "How fast will we see results?",
            a: "Most clients see measurable pipeline improvements within the first 60–90 days. The diagnostic includes a realistic timeline mapped to your specific bottlenecks.",
        },
        {
            q: "Do you replace our sales team?",
            a: "Never. We make your existing team dramatically more effective by automating the busywork and arming reps with AI copilots, so they spend their time on high-value closing.",
        },
        {
            q: "Which CRMs do you integrate with?",
            a: "All the major platforms — HubSpot, Salesforce, Pipedrive, Close and more. If it has an API, we can wire it into your revenue engine.",
        },
        {
            q: "How is pricing structured?",
            a: "Live SKUs have a number: $199 website, Askbox $1,197 setup, HVAC $500+$500/month, CellarHost from $499/month, website care from $29/month. Custom work is a free diagnostic then a fixed proposal. The pricing page has the full table.",
        },
        {
            q: "Is the diagnostic really free?",
            a: "Yes. Thirty minutes, zero commitment. You walk away with the bottleneck and a price whether you hire us or not.",
        },
    ],
};
